import { useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, X, Loader2, CheckCircle2, AlertTriangle, XCircle, ShieldCheck } from "lucide-react";

const sourceBadge = (s, category, item) => {
  // Capabilities fournies par le connecteur backend (multi-CMS) — prioritaire
  if (item && item.source_label !== undefined) {
    return item.writable
      ? { label: `Écriture auto (${item.source_label})`, cls: "bg-blue-50 text-blue-700 border-blue-200" }
      : { label: "Application manuelle", cls: "bg-slate-50 text-slate-600 border-slate-200" };
  }
  if (s === "github") return { label: "Écriture auto (GitHub)", cls: "bg-blue-50 text-blue-700 border-blue-200" };
  if (s === "wix" && ["Titre SEO", "Meta description"].includes(category))
    return { label: "Écriture auto (Wix Blog)", cls: "bg-purple-50 text-purple-700 border-purple-200" };
  if (s === "wix_static" && ["Titre SEO", "Meta description", "Canonical", "Open Graph", "Schema"].includes(category))
    return { label: "Écriture auto (Pont Velo)", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" };
  return { label: "Application manuelle", cls: "bg-slate-50 text-slate-600 border-slate-200" };
};

export function BulkFixWizard({ siteId, issues, onClose, excludedC = 0 }) {
  const [step, setStep] = useState(1);
  const [selected, setSelected] = useState(() => new Set(
    issues.map((_, i) => i).filter((i) => !issues[i].priority || issues[i].priority === "A")
  ));
  const [progress, setProgress] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [applying, setApplying] = useState(false);
  const [results, setResults] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const toggle = (i) => {
    const next = new Set(selected);
    next.has(i) ? next.delete(i) : next.add(i);
    setSelected(next);
  };

  const startGeneration = async () => {
    const items = issues.filter((_, i) => selected.has(i)).map((iss) => ({
      page_url: iss.page_url, category: iss.category,
      message: iss.message, current_value: iss.current_value || null,
    }));
    if (!items.length) return;
    setStep(2);
    try {
      const { data } = await api.post("/seo-fix/suggest-bulk", { site_id: siteId, items });
      let polls = 0;
      pollRef.current = setInterval(async () => {
        polls += 1;
        if (polls > 100) {
          clearInterval(pollRef.current);
          toast.error("La génération prend trop de temps — réessayez avec moins de corrections.");
          setStep(1);
          return;
        }
        try {
          const { data: job } = await api.get(`/content/jobs/${data.job_id}`);
          if (job.progress) setProgress(job.progress);
          if (job.status === "completed") {
            clearInterval(pollRef.current);
            setSuggestions((job.result?.suggestions || []).map((s) => ({
              ...s,
              included: !s.error && !s.keep,
              new_value: s.suggestion || "",
            })));
            setStep(3);
          } else if (job.status === "failed") {
            clearInterval(pollRef.current);
            toast.error(job.error || "Génération échouée");
            setStep(1);
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de démarrer la génération");
      setStep(1);
    }
  };

  const applyAll = async () => {
    const items = suggestions.filter((s) => s.included && !s.keep && s.new_value.trim()).map((s) => ({
      page_url: s.page_url, category: s.category, message: s.message || null,
      old_value: s.current_value || null, new_value: s.new_value,
    }));
    if (!items.length) return;
    setApplying(true);
    try {
      const keptCount = suggestions.filter((s) => s.keep).length;
      const { data } = await api.post("/seo-fix/apply-bulk", {
        site_id: siteId, items,
        campaign_name: `Optimisation — ${new Date().toLocaleDateString("fr-FR")} ${new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`,
        proposed_count: suggestions.length,
        kept_count: keptCount,
      });
      setResults(data.results);
      setStep(4);
      if (data.published > 0) toast.success(`${data.published}/${data.total} correction(s) appliquée(s) réellement`);
      else toast.warning("Aucune écriture automatique possible — voir le détail");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'application en masse");
    } finally {
      setApplying(false);
    }
  };

  const verifyAll = async () => {
    setVerifying(true);
    const updated = [...results];
    let reauditStarted = false;
    for (let i = 0; i < updated.length; i++) {
      if (["published", "velo_pushed"].includes(updated[i].status) && updated[i].fix_id) {
        try {
          const { data } = await api.post(`/seo-fix/verify/${updated[i].fix_id}`);
          if (data.verified) {
            updated[i] = { ...updated[i], status: "verified" };
            if (data.reaudit_started) reauditStarted = true;
          }
        } catch { /* keep status */ }
      }
    }
    setResults(updated);
    setVerifying(false);
    const v = updated.filter((r) => r.status === "verified").length;
    if (v) toast.success(`${v} correction(s) vérifiée(s) publiquement ✓`);
    else toast.warning("Pas encore visible publiquement — réessayez dans 2-3 minutes (propagation).");
    if (reauditStarted) toast.info("Re-audit automatique lancé — le nouveau score apparaîtra dans l'onglet Corrections");
  };

  const statusRow = (r) => {
    if (r.status === "verified") return <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700"><ShieldCheck className="w-3.5 h-3.5" /> Vérifié ✓</span>;
    if (r.status === "published") return <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700"><CheckCircle2 className="w-3.5 h-3.5" /> Publié</span>;
    if (r.status === "velo_pushed") return <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-700"><CheckCircle2 className="w-3.5 h-3.5" /> Pont Velo — à vérifier</span>;
    if (r.status === "bridge_pushed") return <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-700" data-testid="bulk-status-bridge"><CheckCircle2 className="w-3.5 h-3.5" /> Pont Emergent — enregistré ✓ (servi si script installé)</span>;
    if (r.status === "conflict") return <span className="inline-flex items-center gap-1 text-xs font-medium text-orange-700"><AlertTriangle className="w-3.5 h-3.5" /> Conflit — page modifiée depuis l'audit</span>;
    if (r.status === "manual_required") return <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700"><AlertTriangle className="w-3.5 h-3.5" /> Manuel requis</span>;
    if (r.status === "blocked") return <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-600"><XCircle className="w-3.5 h-3.5" /> Bloquée (priorité C — serveur)</span>;
    if (r.status === "rejected") return <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700"><XCircle className="w-3.5 h-3.5" /> Refusée (validation)</span>;
    return <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700"><XCircle className="w-3.5 h-3.5" /> Échec</span>;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="bulk-fix-wizard">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="font-display text-lg font-bold text-slate-950 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#002FA7]" /> Correction en masse avec l'IA
            <span className="text-xs font-normal text-slate-400">— étape {step}/4</span>
          </h2>
          <button onClick={onClose} data-testid="bulk-wizard-close" className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {step === 1 && (
            <>
              <p className="text-sm text-slate-600 mb-2">
                Sélectionnez les problèmes à corriger ({selected.size}/{issues.length} sélectionnés, max 15).
                L'IA génère une suggestion pour chacun — vous validez avant toute écriture.
              </p>
              <div className="mb-4 text-xs text-slate-500" data-testid="bulk-priority-recap">
                Priorités : <strong>{issues.filter((x) => !x.priority || x.priority === "A").length} A</strong> (sûres — cochées par défaut) ·{" "}
                <strong>{issues.filter((x) => x.priority === "B").length} B</strong> (utiles — à cocher manuellement) ·{" "}
                <strong>{excludedC} C exclue(s)</strong> (manuel/sensible — jamais en masse, bloquées aussi côté serveur)
              </div>
              <div className="space-y-2">
                {issues.map((iss, i) => (
                  <label key={i} className="flex items-start gap-3 p-3 border border-slate-200 rounded-md hover:bg-slate-50 cursor-pointer" data-testid={`bulk-issue-${i}`}>
                    <input type="checkbox" checked={selected.has(i)} onChange={() => toggle(i)}
                      data-testid={`bulk-issue-checkbox-${i}`} className="mt-1 accent-[#002FA7]" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-red-600 uppercase tracking-wider">{iss.category}</span>
                        <span className="text-xs text-slate-400 truncate">{iss.page_url}</span>
                      </div>
                      <div className="text-sm text-slate-800">{iss.message}</div>
                    </div>
                  </label>
                ))}
              </div>
            </>
          )}

          {step === 2 && (
            <div className="py-16 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-[#002FA7] mx-auto mb-4" />
              <p className="text-sm text-slate-700 font-medium">Génération des suggestions IA en cours…</p>
              <p className="text-xs text-slate-500 mt-1">{progress ? `Progression : ${progress}` : "Analyse du contenu réel de chaque page"} — peut prendre 1-2 minutes.</p>
            </div>
          )}

          {step === 3 && (
            <>
              <p className="text-sm text-slate-600 mb-4">
                Validez chaque correction (modifiable). Seules les corrections cochées seront appliquées.
              </p>
              <div className="space-y-4">
                {suggestions.map((s, i) => {
                  const badge = sourceBadge(s.source, s.category, s);
                  return (
                    <div key={i} className={`border rounded-md p-4 ${s.included ? "border-slate-300" : "border-slate-200 opacity-60"}`} data-testid={`bulk-review-${i}`}>
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={s.included} disabled={!!s.error || !!s.keep}
                            onChange={() => setSuggestions((prev) => prev.map((p, j) => j === i ? { ...p, included: !p.included } : p))}
                            data-testid={`bulk-review-checkbox-${i}`} className="accent-[#002FA7]" />
                          <span className="text-xs font-semibold text-slate-900 uppercase tracking-wider">{s.category}</span>
                        </label>
                        <div className="flex items-center gap-1.5">
                          {s.page_lang && (
                            <span data-testid={`bulk-lang-${i}`} title="Langue détectée de la page"
                              className="text-[10px] font-bold px-1.5 py-0.5 rounded border border-slate-300 bg-slate-100 text-slate-700 uppercase">
                              {s.page_lang}
                            </span>
                          )}
                          <span className={`text-[10px] px-2 py-0.5 rounded border ${badge.cls}`}>{badge.label}</span>
                        </div>
                      </div>
                      <div className="text-xs text-slate-400 truncate mb-2">{s.page_url}</div>
                      {s.error ? (
                        <div className="text-xs text-red-600">{s.error}</div>
                      ) : s.keep ? (
                        <div className="text-xs bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5 text-emerald-800" data-testid={`bulk-keep-${i}`}>
                          ✓ Analysé — aucune modification nécessaire. <span className="italic">{s.keep_reason}</span>
                        </div>
                      ) : (
                        <>
                          <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Avant</div>
                          <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded px-2 py-1 mb-2">
                            {s.current_value || <em className="text-slate-400">Aucune valeur</em>}
                          </div>
                          <div className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wider">Après (modifiable)</div>
                          <textarea value={s.new_value} rows={2}
                            onChange={(e) => setSuggestions((prev) => prev.map((p, j) => j === i ? { ...p, new_value: e.target.value } : p))}
                            data-testid={`bulk-review-textarea-${i}`}
                            className="w-full border border-emerald-300 bg-emerald-50/40 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400/40" />
                          <div className="text-right text-[10px] text-slate-400">{s.new_value.length} caractères</div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {step === 4 && results && (
            <>
              <p className="text-sm text-slate-600 mb-4">
                Résultat de l'application — seules les écritures réelles sont marquées « Publié ». La vérification confirme la présence sur la page publique.
              </p>
              <div className="space-y-2">
                {results.map((r, i) => (
                  <div key={i} className="flex items-start justify-between gap-3 p-3 border border-slate-200 rounded-md" data-testid={`bulk-result-${i}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-semibold text-slate-900">{r.category}</div>
                      <div className="text-xs text-slate-400 truncate">{r.page_url}</div>
                      {(r.note || r.error) && <div className="text-xs text-slate-600 mt-1">{r.note || r.error}</div>}
                      {r.status === "conflict" && r.current_value && (
                        <div className="text-xs text-orange-700 mt-1">Valeur actuelle en ligne : <em>{r.current_value}</em></div>
                      )}
                    </div>
                    <div className="flex-shrink-0">{statusRow(r)}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between gap-3">
          <div className="text-xs text-slate-500">
            {step === 3 && `${suggestions.filter((s) => s.included).length} correction(s) sélectionnée(s)`}
            {step === 4 && results && (() => {
              const bridge = results.filter((r) => r.status === "bridge_pushed").length;
              const applied = results.filter((r) => ["published", "velo_pushed", "verified"].includes(r.status)).length;
              const failed = results.filter((r) => ["rejected", "failed", "error", "conflict", "blocked"].includes(r.status)).length;
              const manual = results.filter((r) => r.status === "manual_required").length;
              const kept = suggestions.filter((s) => s.keep).length;
              return `${applied + bridge} correction(s) appliquée(s)${bridge > 0 ? ` dont ${bridge} via Pont Emergent` : ""} · ${failed} échec(s) · ${manual} manuel · ${kept} conservée(s)${bridge > 0 ? ` · ${bridge} à vérifier côté Google (Search Console)` : ""}`;
            })()}
          </div>
          <div className="flex gap-2">
            {step === 1 && (
              <button onClick={startGeneration} disabled={selected.size === 0} data-testid="bulk-generate-button"
                className="bg-[#002FA7] hover:bg-[#001D6B] disabled:opacity-50 text-white rounded-md px-4 py-2 text-sm font-medium">
                Générer les suggestions IA ({Math.min(selected.size, 15)})
              </button>
            )}
            {step === 3 && (
              <button onClick={applyAll} disabled={applying || suggestions.filter((s) => s.included && s.new_value.trim()).length === 0}
                data-testid="bulk-apply-button"
                className="bg-[#002FA7] hover:bg-[#001D6B] disabled:opacity-50 text-white rounded-md px-4 py-2 text-sm font-medium inline-flex items-center gap-2">
                {applying && <Loader2 className="w-4 h-4 animate-spin" />}
                {applying ? "Application…" : `Appliquer ${suggestions.filter((s) => s.included && s.new_value.trim()).length} correction(s)`}
              </button>
            )}
            {step === 4 && results?.some((r) => ["published", "velo_pushed"].includes(r.status)) && (
              <button onClick={verifyAll} disabled={verifying} data-testid="bulk-verify-button"
                className="border border-[#002FA7] text-[#002FA7] hover:bg-blue-50 rounded-md px-4 py-2 text-sm font-medium inline-flex items-center gap-2">
                {verifying && <Loader2 className="w-4 h-4 animate-spin" />}
                Vérifier sur les pages publiques
              </button>
            )}
            <button onClick={onClose} className="border border-slate-300 rounded-md px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
              {step === 4 ? "Fermer" : "Annuler"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
